import { useInView } from '../../hooks/useInView';
import { Link } from 'react-router-dom';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface SectionProcessProps {
  heading?: string;
  steps: ProcessStep[];
}

export default function SectionProcess({ heading = 'How It Works', steps }: SectionProcessProps) {
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: stepsRef, isVisible: stepsVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-rdr-cream py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 lg:mb-20 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">{heading}</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl leading-tight">
            Your Journey to an Extraordinary Event
          </h2>
        </div>

        {/* Steps */}
        <div
          ref={stepsRef as React.RefObject<HTMLDivElement>}
          className="relative"
        >
          {/* Connector line — desktop */}
          <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-rdr-gold/40 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-6">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`flex flex-col items-center text-center fade-up ${stepsVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                {/* Number circle */}
                <div className="relative z-10 w-[84px] h-[84px] bg-rdr-cream border-2 border-rdr-gold/50 rounded-full flex items-center justify-center mb-8 group-hover:border-rdr-gold transition-colors duration-300">
                  <span className="font-serif text-3xl text-rdr-gold font-medium">{step.number}</span>
                </div>

                <h3 className="font-serif text-rdr-charcoal text-xl lg:text-2xl mb-3 leading-snug">{step.title}</h3>
                <p className="text-rdr-muted font-sans text-sm leading-relaxed max-w-xs">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 fade-up delay-600 ${stepsVisible ? 'is-visible' : ''}`}>
          <Link
            to="/location#contact"
            className="inline-block bg-rdr-charcoal text-white font-sans text-xs tracking-[0.2em] uppercase px-10 py-4 hover:bg-rdr-warm transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Start Your Journey
          </Link>
        </div>
      </div>
    </section>
  );
}
